import * as React from 'react';

import { Card, CardActions, CardContent, CardHeader } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { useCreateView } from '../../../../sic-lib/api/hooks/useViews';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { buildCreateView, ViewStyle } from '../../../../sic-lib/types/View';
import { ViewProperty } from '../../../../sic-lib/types/ViewProperty';
import { IErrorFormValidation } from '../../../../sic-lib/utils/formValidationHelpers';
import { useAppSelector } from '../../../../store/hooks';
import DraggableViewProperties from '../../../Contact/ContactGroups/DraggableViewProperties/DraggableViewProperties';
import ButtonLoading from '../../../Shared/ButtonLoading';
import TextFieldFast from '../../../Shared/TextFieldFast/TextFieldFast';
import SelectViewProperties from '../SelectViewProperties/SelectViewProperties';

interface FormState {
    viewName: string;
    viewIsGlobal: string;
    viewProperties: ViewProperty[];
    formError: IErrorFormValidation | null;
}

interface Props {
    onDoneHandler: () => void;
}

export default function ClientViewsItemCreate({ onDoneHandler }: Props) {
    const { mutate: createViewMutation, isPending: isCreateViewLoading } = useCreateView();

    const userName = useAppSelector(state => {
        return state.user?.userName || '';
    });

    const [formState, formDispatch] = React.useReducer<GenericReducer<FormState>>(genericReducer, {
        viewName: '',
        viewIsGlobal: '',
        viewProperties: [],
        formError: null,
    });

    const handleChangeGlobal = (event: React.ChangeEvent<HTMLInputElement>) => {
        formDispatch({
            viewIsGlobal: event.target.checked ? userName : '',
        });
    };

    const handleSubmit = async () => {
        try {
            const view = buildCreateView({
                name: formState.viewName,
                style: ViewStyle.list,
                properties: formState.viewProperties,
            });

            createViewMutation(view);

            onDoneHandler();
        } catch (error: any) {
            if (error.isFormError) {
                formDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <Card>
            <CardHeader title="Create Custom View" />
            <Divider />
            <CardContent>
                <TextFieldFast
                    fieldName="viewName"
                    fieldValue={formState.viewName}
                    fullWidth={true}
                    label="View label"
                    variant="outlined"
                    autoFocus={true}
                    error={Boolean(formState.formError?.field === 'name')}
                    helperText={
                        formState.formError?.field === 'name' ? formState.formError.message : ''
                    }
                    onChangeHandler={formDispatch}
                />
                <FormControl>
                    <FormControlLabel
                        label="Global view"
                        labelPlacement="end"
                        control={
                            <Checkbox
                                color="primary"
                                name="viewIsGlobal"
                                checked={Boolean(formState.viewIsGlobal)}
                                onChange={handleChangeGlobal}
                            />
                        }
                    />
                    <FormHelperText>
                        Global views can be accessed by all account users
                    </FormHelperText>
                </FormControl>
                <div className="clientViews__itemSection">
                    <DraggableViewProperties
                        viewProperties={formState.viewProperties}
                        onChangeHandler={formDispatch}
                    />
                </div>
                <div className="clientViews__itemSection">
                    <SelectViewProperties
                        label="Add Property"
                        variant="outlined"
                        fullWidth
                        SelectProps={{
                            MenuProps: { disablePortal: true },
                        }}
                        viewProperties={formState.viewProperties}
                        onSelectHandler={formDispatch}
                        error={Boolean(formState.formError?.field === 'viewProperties')}
                        helperText={
                            formState.formError?.field === 'viewProperties'
                                ? formState.formError.message
                                : ''
                        }
                    />
                </div>
            </CardContent>
            <Divider />
            <CardActions>
                <Button onClick={onDoneHandler}>Cancel</Button>
                <ButtonLoading
                    isLoading={isCreateViewLoading}
                    onClick={handleSubmit}
                    color="secondary"
                    variant="outlined"
                >
                    Save
                </ButtonLoading>
            </CardActions>
        </Card>
    );
}
